import * as Sentry from "@sentry/react";

import {
  useCompletedFormState,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";
import { Form } from "formik";
import { CAR_ABOUT_STEP, CAR_FIND_STEP, CAR_PRICE_STEP } from "..";

import Button from "@storeblocks/button";
import { Icons } from "@storebrand-digital/insurance-definitions";
import { BonusOption, useBonusOptions } from "Api/registry";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { StyledComparisonWrap } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/productPriceTile.styles";
import { AccessibleCallUsMessage } from "Components/Form/AccessibleCallUsMessage";
import { AlertWithIllustration } from "Components/Form/Alert";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { NavigationButtons } from "Components/NavigationButtons";
import { FailedPriceCheckMessage } from "Components/PriceCheckFailedMessage/FailedPriceCheckMessage";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { CarProductIntroFormValues } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductIntro/FormElements/interfaces";
import {
  PrepopulateInfoBox,
  PrepopulateInfoBoxDiv,
  PrepopulateInfoBoxRow,
} from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductPrice/styled";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { useFeatureToggle } from "Store/featureToggles/featureToggleHooks";
import { FeatureToggle } from "Store/featureToggles/featureToggleTypes";
import { BonusTransfer } from "Store/interfaces/CompanyTransferSpecifications";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { CarPriceRequestInfo } from "Store/interfaces/products/checkPrice/riskProducts/VehicleProducts/Car";
import { RENTAL_TYPE_VARIABLE } from "Store/interfaces/products/checkout/riskProducts/vehicleProducts/BaseCarProduct";
import { CalculationPrice } from "Store/priceCalculation/priceCalculationInterfaces";
import { ReduxState } from "Store/rootReducer";
import { prevStep } from "Store/steps/stepsSlice";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { useUserIdentificationSelector } from "Store/userIdentification/userIdentificationSelectors";
import { formBooleanToBoolean } from "Utils/formUtils";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Reason } from "../InvalidInsuranceObject";
import {
  CarOwner,
  CarProductDetailsFormValues,
} from "../ProductDetails/FormElements/interfaces";
import { DEFAULT_DEDUCTIBLE_CAR, INVALID_OBJECT } from "../carConstants";
import { FormElements } from "./FormElements/FormElements";
import { validationSchema } from "./carProductPriceValidation";
import { CarPriceFormValues } from "./interfaces";

interface Props {
  readonly updatePriceRequest: (
    payload: CarPriceRequestInfo,
    stepForwardOnSuccess?: boolean,
  ) => void;
}

export const CarProductPrice = (props: Props): React.ReactElement => {
  const { updatePriceRequest } = props;
  const handleAddProductToCart = useAddProductToCartHandler(CAR_PRICE_STEP.id);
  const { getText, getRadioOptions } = useTexts();

  const introFormValues = useCompletedFormState<CarProductIntroFormValues>(
    CAR_FIND_STEP.id,
  );
  const aboutFormState = useFormState<CarProductDetailsFormValues>(
    CAR_ABOUT_STEP.id,
  );
  const formState = useFormState<CarPriceFormValues>(
    CAR_PRICE_STEP.id,
    initialPriceFormState,
  );
  const [
    {
      formText: { checkPrice },
    },
    lang,
  ] = useProductTexts(getFMSProductName(INSURANCE_NAME_VALUE.CAR));
  const userIdentification = useUserIdentificationSelector();
  const priceCalculation = useSelector(
    (state: ReduxState) => state.priceCalculation.priceCalculationResult,
  );
  const { addProductToCart } = useSelector((state: ReduxState) => state);
  const { bonusPreOwnership } =
    useCompletedFormState<CarProductDetailsFormValues>(CAR_ABOUT_STEP.id);

  const history = useHistory();

  const handleSubmit = (formProps: CarPriceFormValues): void => {
    if (formState.carOwner === CarOwner.OTHER && blockingPage) {
      return history.push(INVALID_OBJECT, { reason: Reason.INVALID_OWNER });
    }
    const prices = priceCalculation.current?.[formProps.coverage]
      ?.prices as Array<CalculationPrice>;
    const price =
      prices.find(({ value }) => value === formProps.deductible) || prices[0];

    const productToAdd = {
      coverage: formProps.coverage,
      productType: INSURANCE_NAME_VALUE.CAR,
      filterValue: price.value,
    };
    const tfaAmount = priceCalculation.current?.[formProps.coverage]?.tfaAmount;
    handleAddProductToCart({ ...formProps, tfaAmount }, [productToAdd]);
  };

  const currentBonus = useGetBonusForCustomer(Number(aboutFormState.bonus));
  const blockingPage = useFeatureToggle(FeatureToggle.BLOCKING_PAGE);

  const damageOptions = getRadioOptions(
    TextComponentNameLevel1.RECENT_DAMAGES,
    "injuries",
  );

  const damageOption = damageOptions.find(
    (item) => item.value === aboutFormState.injuries,
  );

  const variables: PopulatedVariable[] = [
    {
      label: getText("carform.label.bonushasfrommyselfprepopulated"),
      value: currentBonus?.displayText ?? "",
    },
    {
      label: getText("carform.label.injuriesprepopulated"),
      value: damageOption?.label ?? "",
    },
    {
      label: getText("carform.label.morgageeprepopulated"),
      value: formBooleanToBoolean(aboutFormState.mortgagee)
        ? getText("optionlabel.booleanyes")
        : getText("optionlabel.booleanno"),
    },
  ];
  const priceRequestFailed = priceCalculation.error !== null;

  if (priceRequestFailed) {
    return (
      <FailedPriceCheckMessage
        isLoading={priceCalculation.isLoading}
        updatePriceRequest={updatePriceRequest}
      />
    );
  }

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.CAR}`,
        );
      }}
    >
      <>
        <ProductPriceTile
          initialValues={formState}
          onSubmit={handleSubmit}
          validationSchema={validationSchema(
            introFormValues.brand,
            introFormValues.year,
            userIdentification,
          )}
          header={checkPrice.header[lang]}
        >
          <Form>
            {aboutFormState.automaticallyPopulated && (
              <AutomaticallyPrepopulatedValues variables={variables} />
            )}
            <FormElements
              updatePriceRequest={updatePriceRequest}
              texts={checkPrice}
              lang={lang}
            />

            {addProductToCart.error && (
              <SystemErrorAlert alertId="addProductToCart" marginTop="32px" />
            )}

            <ScrollToFirstError />

            {bonusPreOwnership === BonusTransfer.UNKNOWN && (
              <AlertWithIllustration
                title={getText("carform.alerttitle.bonuspreownership-unknown")}
                iconName={Icons.CAR}
                marginBottom="24px"
              >
                <p>
                  {getText(
                    "carform.alertdescription.bonuspreownership-unknown",
                  )}
                </p>
                <AccessibleCallUsMessage />
              </AlertWithIllustration>
            )}

            <StyledComparisonWrap>
              <NavigationButtons
                isUpsell
                disableNextButton={bonusPreOwnership === BonusTransfer.UNKNOWN}
                isLoading={
                  addProductToCart.isLoading || priceCalculation.isLoading
                }
              />
            </StyledComparisonWrap>
          </Form>
        </ProductPriceTile>
      </>
    </Sentry.ErrorBoundary>
  );
};

export const initialPriceFormState: Partial<CarPriceFormValues> = {
  deductible: DEFAULT_DEDUCTIBLE_CAR,
  yearlyKmNeed: "2",
  usedByYoungerDriver: BooleanFormType.FALSE,
  isCarTrimmed: undefined,
  rentalType: RENTAL_TYPE_VARIABLE.NONE,
  coverage: undefined,
  carOwner: undefined,
  carOwnerAge: undefined,
};

const useGetBonusForCustomer = (bonusCode: number): undefined | BonusOption => {
  const { data } = useBonusOptions();
  if (!data) {
    return undefined;
  }
  return data.find((item: any) => item.code === bonusCode);
};

type PopulatedVariable = {
  label: string;
  value: string;
};

interface AutomaticallyPrepopulateProps {
  variables: PopulatedVariable[];
}
export const AutomaticallyPrepopulatedValues = ({
  variables,
}: AutomaticallyPrepopulateProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText } = useTexts();

  return (
    <PrepopulateInfoBox>
      <h4> {getText("common.price.prepopulate.heading")}</h4>
      <span>{getText("common.price.prepopulate.ingress")} </span>
      <PrepopulateInfoBoxDiv>
        {variables.map((variable) => (
          <PrepopulateInfoBoxRow key={variable.label}>
            <b>{variable.label}:</b> {variable.value}
          </PrepopulateInfoBoxRow>
        ))}
      </PrepopulateInfoBoxDiv>

      <Button
        variant="outlined"
        type="button"
        iconRight="edit"
        paddingLeft="0"
        onClick={() => {
          dispatch(prevStep());
        }}
      >
        {getText("prepopulate.editbutton")}
      </Button>
    </PrepopulateInfoBox>
  );
};
